import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QrCode, X, Camera } from "lucide-react";
import { createScanConfirm } from "@/components/barcode/scanConfirm";

/**
 * BarcodeScanner — opens a dialog with a live camera feed and scans barcodes / QR codes.
 * Uses html5-qrcode which supports EAN-13, Code-128, QR, and many other formats on
 * Android/iOS/Desktop. Requires HTTPS + camera permission.
 */
// Tek hedef alan: kütüphanenin gerçek tarama bölgesi (shaded region) görünür çerçevedir; video kırpılmaz
const scanBox = (vw, vh) => {
  const width = Math.max(120, Math.floor(Math.min(vw * 0.86, 440)));
  const height = Math.max(80, Math.floor(Math.min(width * 0.58, vh * 0.72)));
  return { width, height };
};
const VIEW = "[&_video]:!w-full [&_video]:!h-auto [&_video]:block [&_#qr-shaded-region>div]:!bg-amber-400";
// Tüm tarayıcı oturumlarında ortak: tek kare kabul edilmez (3 ardışık aynı okuma ≈0.3sn), kabul sonrası 1.5sn aynı kod yok sayılır
const confirmScan = createScanConfirm({ need: 3, windowMs: 1200, cooldownMs: 1500 });

export default function BarcodeScanner({ open, onOpenChange, onDetected }) {
  const containerId = "html5-barcode-scanner";
  const scannerRef = useRef(null);
  const lockedRef = useRef(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    let disposed = false;
    if (!open) return;
    lockedRef.current = false;

    (async () => {
      setError("");
      setStarting(true);
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (disposed) return;
        // Small delay so the container div is mounted
        await new Promise((r) => setTimeout(r, 50));
        const scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, aspectRatio: 4 / 3, qrbox: scanBox },
          (decodedText) => {
            const code = confirmScan(decodedText);
            if (!code || lockedRef.current) return;
            lockedRef.current = true;
            onDetected?.(code);
            stop();
            onOpenChange?.(false);
            toast.success(`Kod okundu: ${code}`);
          },
          () => { /* per-frame errors ignored */ }
        );
      } catch (e) {
        setError(e?.message || "Kamera başlatılamadı. Tarayıcı izinlerini kontrol edin.");
      } finally {
        setStarting(false);
      }
    })();

    const stop = async () => {
      const s = scannerRef.current;
      if (s) {
        try { await s.stop(); } catch {}
        try { s.clear(); } catch {}
        scannerRef.current = null;
      }
    };

    return () => {
      disposed = true;
      stop();
    };
    // eslint-disable-next-line
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-800">
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <QrCode className="w-5 h-5 text-blue-500" />
            Barkod / QR Tarayıcı
          </DialogTitle>
          <div className="text-xs text-slate-400 mt-0.5">
            Kameranın önüne barkodu veya QR kodu getirin.
          </div>
        </DialogHeader>

        <div className="p-4">
          <div className="relative min-h-[180px] bg-slate-900 border border-slate-800 rounded-sm overflow-hidden" data-testid="scanner-viewport">
            <div id={containerId} className={`relative w-full ${VIEW}`} />
            {starting && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400 text-xs pointer-events-none">
                <Camera className="w-8 h-8 animate-pulse text-blue-500" />
                Kamera başlatılıyor…
              </div>
            )}
          </div>
          <div className="mt-2 text-xs text-slate-400 text-center" data-testid="scanner-hint">Barkodu sarı köşeli alanın içine yatay hizalayın.</div>

          {error && (
            <div className="mt-3 text-xs text-red-400 border border-red-900/50 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="scanner-error">
              {error} — Barkodu aşağıya elle yazabilir veya Ürün Ara ile devam edebilirsiniz.
            </div>
          )}

          <form className="mt-3 flex gap-2" onSubmit={(e) => {
            e.preventDefault();
            const c = manual.trim();
            if (!c) return;
            onDetected?.(c); setManual(""); onOpenChange?.(false);
          }}>
            <input value={manual} onChange={(e) => setManual(e.target.value)} inputMode="numeric" placeholder="Barkod numarasını elle girin"
              className="flex-1 h-10 rounded-sm border border-slate-700 bg-slate-900 px-3 text-sm font-mono text-slate-100" data-testid="scanner-manual-input" />
            <Button type="submit" className="h-10 rounded-sm bg-blue-600 hover:bg-blue-500" data-testid="scanner-manual-submit">Kullan</Button>
          </form>

          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
              data-testid="close-scanner-btn"
            >
              <X className="w-4 h-4 mr-2" /> Kapat
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
